import { useMemo, useState } from "react";
import { ChevronsUpDown, Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { DASH, dash, formatCompactINR, formatDate, formatNumber } from "@/lib/cibil/format";
import { enquiryVelocity } from "@/lib/cibil/metrics";
import type { NormalizedEnquiry, NormalizedReport } from "@/lib/cibil/normalize";

type SortKey = "date" | "lender" | "purpose" | "amount";

const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: "date", label: "Date" },
  { key: "lender", label: "Enquiring Member" },
  { key: "purpose", label: "Purpose" },
  { key: "amount", label: "Amount", numeric: true },
];

function daysAgo(date: Date | null, reference: Date): number | null {
  if (!date) return null;
  return Math.max(0, Math.round((reference.getTime() - date.getTime()) / 86_400_000));
}

function EnquiryTable({
  enquiries,
  reference,
}: {
  enquiries: NormalizedEnquiry[];
  reference: Date;
}) {
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "date",
    dir: "desc",
  });

  const rows = useMemo(() => {
    const value = (e: NormalizedEnquiry): string | number => {
      switch (sort.key) {
        case "date":
          return e.date?.getTime() ?? 0;
        case "amount":
          return e.amount ?? -1;
        case "lender":
          return (e.lender ?? "").toLowerCase();
        default:
          return (e.purpose ?? "").toLowerCase();
      }
    };
    return [...enquiries].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va === vb) return 0;
      const cmp =
        typeof va === "number" && typeof vb === "number" ? va - vb : String(va) > String(vb) ? 1 : -1;
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [enquiries, sort]);

  const toggle = (key: SortKey) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            {COLUMNS.map((c) => (
              <TableHead key={c.key} className={cn("whitespace-nowrap", c.numeric && "text-right")}>
                <button
                  type="button"
                  onClick={() => toggle(c.key)}
                  className={cn(
                    "inline-flex items-center gap-1 hover:text-foreground",
                    sort.key === c.key && "text-foreground",
                  )}
                >
                  {c.label}
                  <ChevronsUpDown className="size-3 opacity-60" />
                </button>
              </TableHead>
            ))}
            <TableHead className="text-right whitespace-nowrap">Days Ago</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                No enquiries reported.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((e) => {
              const d = daysAgo(e.date, reference);
              return (
                <TableRow key={e.id}>
                  <TableCell className="whitespace-nowrap">{formatDate(e.date)}</TableCell>
                  <TableCell className="font-medium">{dash(e.lender)}</TableCell>
                  <TableCell className="whitespace-nowrap">{dash(e.purpose)}</TableCell>
                  <TableCell className="text-right tabular-nums whitespace-nowrap">
                    {formatCompactINR(e.amount)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums whitespace-nowrap text-muted-foreground">
                    {d === null ? DASH : `${formatNumber(d)}d`}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export function EnquiriesBanner({
  report,
  countLastYear,
  referenceDate,
}: {
  report: NormalizedReport;
  countLastYear: number;
  referenceDate: Date;
}) {
  const enquiries = report.enquiries;
  const velocity = useMemo(
    () => enquiryVelocity(report, referenceDate),
    [report, referenceDate],
  );
  const totalAmount = enquiries.reduce((a, e) => a + (e.amount ?? 0), 0);
  const openedLast12 = useMemo(() => {
    const cutoff = new Date(
      referenceDate.getFullYear() - 1,
      referenceDate.getMonth(),
      referenceDate.getDate(),
    );
    return report.accounts.filter((a) => a.sanctionDate && a.sanctionDate >= cutoff).length;
  }, [report.accounts, referenceDate]);
  const ratio = openedLast12 > 0 ? countLastYear / openedLast12 : null;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-warning-soft/60 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-background/70">
          <Search className="size-3.5" />
        </span>
        <div className="grid gap-2">
          <p className="text-sm">
            There {countLastYear === 1 ? "is" : "are"}{" "}
            <strong>{formatNumber(countLastYear)}</strong> bureau{" "}
            {countLastYear === 1 ? "enquiry" : "enquiries"} in the last 1 year.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {velocity.map((v) => (
              <Badge key={v.label} variant="secondary" className="tabular-nums">
                {v.label}: {formatNumber(v.count)}
              </Badge>
            ))}
            <Badge variant="outline" className="tabular-nums">
              Enquired amount {formatCompactINR(totalAmount)}
            </Badge>
            <Badge variant="outline" className="tabular-nums">
              Enquiry-to-new-account (12m) {ratio === null ? DASH : `${ratio.toFixed(1)}x`}
            </Badge>
          </div>
        </div>
      </div>
      <Sheet>
        <SheetTrigger asChild>
          <Button size="sm" variant="outline" className="shrink-0 self-start lg:self-auto">
            View Details
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>Bureau Enquiries</SheetTitle>
            <SheetDescription>
              {formatNumber(enquiries.length)} enquiries reported on this bureau record.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6">
            <EnquiryTable enquiries={enquiries} reference={referenceDate} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
